import axios from "axios";
import { showAlert, showConfirm, handleRequestError } from "../../utils/alert";
import flatpickr from "flatpickr";
import "flatpickr/dist/themes/light.css";
import { Portuguese } from "flatpickr/dist/l10n/pt.js";

const toLocalDateStr = (dateObj) => {
  return (
    dateObj.getFullYear() +
    "-" +
    String(dateObj.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(dateObj.getDate()).padStart(2, "0")
  );
};

export default () => ({
  isLoading: true,
  isProcessingAction: false,
  orders: [],
  filteredOrders: [],
  search: "",

  // Modal de Detalhes
  isDetailsModalOpen: false,
  selectedOrder: null,

  // Controle de Datas
  startDate: "",
  endDate: "",
  fpStartInstance: null,
  fpEndInstance: null,

  async initHistory() {
    const token = localStorage.getItem("jwt_token");
    if (!token) {
      window.location.href = "/client/login";
      return;
    }
    window.axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    try {
      this.setThisMonth(false);
      this.initDatePickers();
      await this.fetchOrders();
    } catch (error) {
      if (error.response && error.response.status === 403) {
        this.hasAccessModule = false;
        return;
      }
      if (error.response && [401, 402].includes(error.response.status)) return;
      console.error("Infrastructure error:", error);
    }
  },

  initDatePickers() {
    this.fpStartInstance = flatpickr("#history-start-date", {
      locale: Portuguese,
      dateFormat: "Y-m-d",
      altInput: true,
      altFormat: "d/m/Y",
      defaultDate: this.startDate,
      onChange: (selectedDates, dateStr) => {
        this.startDate = dateStr;
      },
    });

    this.fpEndInstance = flatpickr("#history-end-date", {
      locale: Portuguese,
      dateFormat: "Y-m-d",
      altInput: true,
      altFormat: "d/m/Y",
      defaultDate: this.endDate,
      onChange: (selectedDates, dateStr) => {
        this.endDate = dateStr;
      },
    });
  },

  // ================= AÇÕES DO MODAL =================
  openDetails(order) {
    this.selectedOrder = order;
    this.isDetailsModalOpen = true;
  },

  closeDetails() {
    this.isDetailsModalOpen = false;
    this.selectedOrder = null;
  },

  // ================= CANCEL LOGIC =================
  async cancelOrder(order) {
    // Show SweetAlert confirmation to the user
    const confirmation = await showConfirm(
      "Cancelar Ordem de Serviço?",
      "Esta ação devolverá os produtos ao estoque, reabrirá a agenda e estornará os valores no financeiro. Esta ação não pode ser desfeita.",
    );

    if (!confirmation.isConfirmed) return;

    this.isProcessingAction = true;

    try {
      const response = await axios.post(
        `/api/client/service-orders-history/${order.id}/cancel`,
      );

      showAlert(response.data.message, "success");

      // Update the local state to reflect the cancellation immediately without refetching
      const orderIndex = this.orders.findIndex((o) => o.id === order.id);
      if (orderIndex !== -1) {
        this.orders[orderIndex].status = "canceled";
      }

      // Close the modal
      this.closeDetails();

      // Re-trigger the filter to update visual lists and summary cards
      this.filterOrders();
    } catch (error) {
      handleRequestError(error, "Erro ao tentar cancelar a Ordem de Serviço.");
    } finally {
      this.isProcessingAction = false;
    }
  },

  // ================= ATALHOS DE DATAS =================
  setToday(fetchData = true) {
    const today = new Date();
    this.updateDates(toLocalDateStr(today), toLocalDateStr(today), fetchData);
  },

  setYesterday(fetchData = true) {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    this.updateDates(
      toLocalDateStr(yesterday),
      toLocalDateStr(yesterday),
      fetchData,
    );
  },

  setThisMonth(fetchData = true) {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    this.updateDates(
      toLocalDateStr(firstDay),
      toLocalDateStr(lastDay),
      fetchData,
    );
  },

  updateDates(start, end, fetchData) {
    this.startDate = start;
    this.endDate = end;

    if (this.fpStartInstance) this.fpStartInstance.setDate(start);
    if (this.fpEndInstance) this.fpEndInstance.setDate(end);

    if (fetchData) this.fetchOrders();
  },

  async fetchOrders() {
    try {
      this.isLoading = true;
      const response = await axios.get("/api/client/service-orders-history", {
        params: {
          start_date: this.startDate,
          end_date: this.endDate,
        },
      });
      this.orders = response.data.data;
      this.filteredOrders = this.orders;
    } catch (error) {
      console.error("Erro ao buscar histórico", error);
      showAlert("Não foi possível carregar o histórico de vendas.", "error");
    } finally {
      this.isLoading = false;
    }
  },

  filterOrders() {
    if (this.search.trim() === "") {
      this.filteredOrders = this.orders;
      return;
    }

    const lowerSearch = this.search.toLowerCase();
    this.filteredOrders = this.orders.filter((o) => {
      const customer = o.customer_name ? o.customer_name.toLowerCase() : "";
      const id = o.id.toString();
      return customer.includes(lowerSearch) || id.includes(lowerSearch);
    });
  },

  get totalRevenue() {
    return this.filteredOrders
      .filter((o) => o.status === "paid")
      .reduce((sum, o) => sum + parseFloat(o.net_total), 0);
  },

  get totalSales() {
    return this.filteredOrders.filter((o) => o.status === "paid").length;
  },

  formatMoney(value) {
    return parseFloat(value || 0).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  },

  formatDate(dateString) {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return (
      date.toLocaleDateString("pt-BR") +
      " às " +
      date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
    );
  },

  getStatusBadge(status) {
    const badges = {
      open: { class: "bg-orange-100 text-orange-700", label: "Aberta" },
      paid: { class: "bg-green-100 text-green-700", label: "Paga" },
      canceled: { class: "bg-red-100 text-red-700", label: "Cancelada" },
    };
    return (
      badges[status] || { class: "bg-slate-100 text-slate-700", label: status }
    );
  },
});
